import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Picker, Space, Button, Checkbox } from 'antd-mobile';
import { Input, Form } from 'antd';
import { DownOutline } from 'antd-mobile-icons';
import FailModal from 'utility/FailModal';
import { WebviewHttpHandler } from 'shared/webviewHttpHandler';
import { CryptoHandler } from 'shared/cryptoHandler';
import secureStorage from 'shared/secureStorage';

const webviewHttpHandler = new WebviewHttpHandler(true);
const cryptoHandler = new CryptoHandler();
const QuestionContainer = ({ title, children }) => (
  <div className="q-container">
    <div className="mb-6">
      <div className="text-body font-semibold">
        <span style={{ color: 'red' }}>* </span>
        {title}
      </div>
    </div>
    {children}
  </div>
);
// -----------------------------------------------------

export const basicColumns = [
  [
    { label: '北部', value: '北部' },
    { label: '中部', value: '中部' },
    { label: '南部', value: '南部' },
    { label: '東部', value: '東部' },
  ],
];

// -----------------------------------------------------
const PreferenceQuestionnaire = () => {
  const [errMsg, setErrMsg] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // 送出問卷
  const handleFinish = () => {
    setLoading(true);
    // 加上line user id, 另外姓名、市話、手機加密
    let postData = {
      ...form.getFieldsValue(),
      line_user_id: secureStorage.getItem('lineUserId'),
      name: cryptoHandler.encrypt(form.getFieldValue('name')),
    };
    if (form.getFieldValue('telephone')) {
      postData.telephone = cryptoHandler.encrypt(
        form.getFieldValue('telephone')
      );
    }
    if (form.getFieldValue('cellphone')) {
      postData.cellphone = cryptoHandler.encrypt(
        form.getFieldValue('cellphone')
      );
    }
    // call API
    webviewHttpHandler
      .post('/webview/create_survey_answer', postData)
      .then((res) => {
        if (res.success) {
          if ('short_rent_url' in res.data) {
            // 跳轉到短租官方帳號
            window.open(res.data.short_rent_url);
          } else {
            // 跳轉到推薦專案並帶上使用者有興趣的品牌
            navigate('/m/RecommendProject', {
              state: { brandPreference: res.data.brand_preference },
            });
          }
          // 跳轉到完成問卷頁面(以流程圖為主，該頁先保留)
          // navigate('/m/PreferenceQuestionnaireSuccess');
        } else {
          setErrMsg(res.detail);
          setIsFailedModalVisible(true);
        }
        setLoading(false);
      });
  };
  // 区域选择状态和逻辑
  const [pickerVisible, setPickerVisible] = useState(false);

  const handleRegionChange = (value) => {
    form.setFieldValue('region', value[0]);
    setPickerVisible(false); // 关闭区域 Picker
  };
  // 聯絡資訊
  const [contactMethod, setContactMethod] = useState('');
  const [contactPickerVisible, setContactPickerVisible] = useState(false);
  const [contactNameByMethod, setContactNameByMethod] = useState('');
  const [contactPlaceholder, setContactPlaceholder] =
    useState('請輸入聯絡資訊');
  const contactMethodOptions = [
    [
      { label: '手機', value: '手機' },
      { label: '市話', value: '市話' },
      { label: 'E-mail', value: 'E-mail' },
    ],
  ];
  // 提交預約表單失敗Modal
  const [isFailedModalVisible, setIsFailedModalVisible] = useState(false);
  // 興趣資訊
  const [isLongTerm, setIsLongTermRent] = useState(false);
  // 生成興趣資訊、車款偏好、品牌偏好的map
  function generateValuesMap(start, length) {
    const valuesMap = {};
    let value = Math.pow(10, start);
    for (let i = 1; i <= length; i++) {
      valuesMap[i] = value;
      value /= 10;
    }
    return valuesMap;
  }
  const rentInfoValuesMap = generateValuesMap(4, 4);
  const carPreferValuesMap = generateValuesMap(3, 4);
  const brandPreferValuesMap = generateValuesMap(5, 6);
  // 處理checkbox的更改
  const handleCheckboxChange = (
    checkedValues,
    valuesMap,
    fieldName,
    padLength
  ) => {
    let value = 0;
    // 計算選中的值
    for (const item of checkedValues) {
      value += valuesMap[item];
    }
    // int轉str, 並且在左側補零
    value = value.toString().padStart(padLength, '0');
    if (value === '0'.repeat(padLength)) {
      form.setFieldValue(fieldName, undefined);
    } else {
      // 如果是租車資訊，個位數代表"皆有"，如果四個選項都選到，則將個位數改為1
      if (fieldName === 'interest_info') {
        if (value === '11110') {
          value = '11111';
        }
      }
      form.setFieldValue(fieldName, value);
    }
  };
  // 處理租車偏好
  const handleRentInfoChange = (checkedValues) => {
    // 如果選到長租相關，則顯示車款偏好、品牌偏好
    if (checkedValues.includes(2) || checkedValues.includes(4)) {
      setIsLongTermRent(true);
    } else {
      setIsLongTermRent(false);
      form.setFieldValue('car_preference', undefined);
      form.setFieldValue('brand_preference', undefined);
    }
    handleCheckboxChange(checkedValues, rentInfoValuesMap, 'interest_info', 5);
  };
  // 處理車款偏好
  const handleCarPrefer = (checkedValues) => {
    handleCheckboxChange(
      checkedValues,
      carPreferValuesMap,
      'car_preference',
      4
    );
  };
  // 處理品牌偏好
  const handleBrandPrefer = (checkedValues) => {
    handleCheckboxChange(
      checkedValues,
      brandPreferValuesMap,
      'brand_preference',
      6
    );
  };
  // 处理联系方式的更改
  const handleContactMethodChange = (value) => {
    form.setFieldValue(contactNameByMethod, undefined);
    setContactMethod(value[0]);
    if (value[0] === '手機') {
      setContactNameByMethod('cellphone');
    } else if (value[0] === '市話') {
      setContactNameByMethod('telephone');
    } else if (value[0] === 'E-mail') {
      setContactNameByMethod('email');
    }
    // 根据选择设置相应的 placeholder
    switch (value[0]) {
      case '手機':
        setContactPlaceholder('請輸入手機號碼');
        break;
      case '市話':
        setContactPlaceholder('請輸入市話號碼');
        break;
      case 'E-mail':
        setContactPlaceholder('請輸入電子郵件');
        break;
      default:
        setContactPlaceholder('請選擇聯絡方式');
    }
    setContactPickerVisible(false); // 关闭 Picker
  };

  return (
    <div className="main-container">
      <FailModal
        isVisible={isFailedModalVisible}
        setIsVisible={setIsFailedModalVisible}
        title="提交失敗"
        message={errMsg}></FailModal>
      <div className="text-align-center">
        <div className="heading font-bold">用車喜好問卷</div>
        <p className="text-body font-normal">
          租期/車款/品牌
          <br />
          協助您快速找到好方案！
        </p>
      </div>
      <Form form={form} onFinish={handleFinish}>
        <Space
          className="mb-30"
          block
          direction="vertical"
          style={{ '--gap': '16px' }}>
          <QuestionContainer title="您的姓名">
            <Form.Item
              name="name"
              rules={[{ required: true, message: '請輸入姓名' }]}>
              <Input className="baseline-input" />
            </Form.Item>
          </QuestionContainer>
          <QuestionContainer title="聯絡方式">
            <Input
              readOnly
              className="large-input mb-6"
              placeholder="請選擇聯絡方式"
              value={contactMethod}
              onClick={() => setContactPickerVisible(true)}
              suffix={<DownOutline style={{ fontSize: '16px' }} />}
            />
            <Picker
              columns={contactMethodOptions}
              visible={contactPickerVisible}
              onClose={() => setContactPickerVisible(false)}
              onConfirm={handleContactMethodChange}
            />
            {/* 动态禁用的 Input */}
            <Form.Item
              name={contactNameByMethod}
              rules={[{ required: true, message: '請輸入聯絡資訊' }]}>
              <Input
                className="baseline-input"
                placeholder={contactPlaceholder}
                disabled={!contactMethod} // 当 contactMethod 为空时禁用
              />
            </Form.Item>
          </QuestionContainer>
          <QuestionContainer title="區域">
            <Picker
              columns={basicColumns}
              visible={pickerVisible}
              onClose={() => setPickerVisible(false)}
              onConfirm={handleRegionChange}
            />
            <Form.Item
              name="region"
              rules={[{ required: true, message: '請選擇區域' }]}>
              <Input
                readOnly
                className="large-input"
                placeholder="請選擇"
                onClick={() => setPickerVisible(true)}
                suffix={<DownOutline style={{ fontSize: '16px' }} />}
              />
            </Form.Item>
          </QuestionContainer>
          <Form.Item
            name="interest_info"
            rules={[{ required: true, message: '請選擇有興趣的租車資訊' }]}>
            <QuestionContainer title="您有興趣的租車資訊（可多選）">
              <Checkbox.Group onChange={handleRentInfoChange}>
                <Space block direction="vertical" style={{ '--gap': '16px' }}>
                  <Checkbox value={1}>個人短期租車</Checkbox>
                  <Checkbox value={2}>個人長期租車</Checkbox>
                  <Checkbox value={3}>公司短期租車</Checkbox>
                  <Checkbox value={4}>公司長期租車</Checkbox>
                </Space>
              </Checkbox.Group>
            </QuestionContainer>
          </Form.Item>
          {isLongTerm && (
            <>
              <Form.Item
                name="car_preference"
                rules={[
                  {
                    required: isLongTerm ? true : false,
                    message: '請選擇偏好車款',
                  },
                ]}>
                <QuestionContainer title="您偏好的車款（可多選）">
                  <Checkbox.Group onChange={handleCarPrefer}>
                    <Space
                      block
                      direction="vertical"
                      style={{ '--gap': '16px' }}>
                      <Checkbox value={1}>轎車、休旅車</Checkbox>
                      <Checkbox value={2}>電動車</Checkbox>
                      <Checkbox value={3}>商用車</Checkbox>
                      <Checkbox value={4}>重機</Checkbox>
                    </Space>
                  </Checkbox.Group>
                </QuestionContainer>
              </Form.Item>
              <Form.Item
                name="brand_preference"
                rules={[
                  {
                    required: isLongTerm ? true : false,
                    message: '請選擇偏好品牌',
                  },
                ]}>
                <QuestionContainer title="您偏好的品牌（可多選）">
                  <Checkbox.Group onChange={handleBrandPrefer}>
                    <Space
                      block
                      direction="vertical"
                      style={{ '--gap': '16px' }}>
                      <Checkbox value={1}>TOYOTA</Checkbox>
                      <Checkbox value={2}>LEXUS</Checkbox>
                      <Checkbox value={3}>TESLA</Checkbox>
                      <Checkbox value={4}>BENZ、BMW</Checkbox>
                      <Checkbox value={5}>VOLVO</Checkbox>
                      <Checkbox value={6}>其他品牌</Checkbox>
                    </Space>
                  </Checkbox.Group>
                </QuestionContainer>
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Button
              block
              className="primary-button"
              type="primary"
              htmlType="submit"
              loading={loading}>
              送 出
            </Button>
          </Form.Item>
        </Space>
      </Form>
    </div>
  );
};

export default PreferenceQuestionnaire;
